import { Link } from 'react-router-dom';
import { TableColumn } from 'shared-ui/lib/customs/react/generic-table/TableColumn';
import { formatDateTime } from 'shared-ui/lib/utils/format-date';
import { routesConfig } from '../config/routes.config';
import { ServiceCallApplicationsQuery } from '../generated/graphql';

type Row = ServiceCallApplicationsQuery['aServiceCallApplications']['list'][0];

const statusLabels: Record<string, string> = {
  pending: 'Odottaa',
  approved: 'Hyväksytty',
  rejected: 'Hylätty',
  inactive: 'Ei aktiivinen',
};

const officialName: TableColumn = {
  field: 'officialName',
  text: 'ServiceCallApplication:officialName',
};

const businessId: TableColumn = {
  field: 'businessId',
  text: 'ServiceCallApplication:businessId',
};

const contactName: TableColumn = {
  field: 'contactName',
  text: 'ServiceCallApplication:contactName',
};

const email: TableColumn = {
  field: 'email',
  text: 'ServiceCallApplication:email',
};

const status: TableColumn = {
  field: 'status',
  text: 'ServiceCallApplication:status',
  formatter: (data: Row) => statusLabels[data.status] ?? data.status,
};

const createdAt: TableColumn = {
  field: 'createdAt',
  text: 'ServiceCallApplication:createdAt',
  formatter: (data: Row) => formatDateTime(data.createdAt),
};

const handledAt: TableColumn = {
  field: 'handledAt',
  text: 'ServiceCallApplication:handledAt',
  formatter: (data: Row) => (data.handledAt ? formatDateTime(data.handledAt) : ''),
};

const link: TableColumn = {
  field: 'id',
  text: 'General:link',
  formatter: (data: Row) => (
    <Link
      to={routesConfig.SERVICE_CALL_APPLICATION.SHOW.replace(':id', data.id)}
      className="hover:text-primary text-gray-color"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M14 5l7 7m0 0l-7 7m7-7H3"
        />
      </svg>
    </Link>
  ),
};

type ActionHandlers = {
  onApprove: (row: Row) => void;
  onReject: (row: Row) => void;
  onRevokeAccess: (row: Row) => void;
  onReactivate: (row: Row) => void;
  labels: {
    approve: string;
    reject: string;
    revokeAccess: string;
    reactivate: string;
  };
};

const actions = ({
  onApprove,
  onReject,
  onRevokeAccess,
  onReactivate,
  labels,
}: ActionHandlers): TableColumn => ({
  field: 'id',
  text: 'ServiceCallApplication:actions',
  formatter: (data: Row) => (
    <div className="flex flex-wrap gap-2">
      {data.status === 'pending' ? (
        <>
          <button
            type="button"
            onClick={() => onApprove(data)}
            className="py-1 px-3 rounded-md text-sm font-medium text-white bg-primary hover:bg-secondary"
          >
            {labels.approve}
          </button>
          <button
            type="button"
            onClick={() => onReject(data)}
            className="py-1 px-3 rounded-md text-sm font-medium text-white bg-red-500 hover:bg-red-600"
          >
            {labels.reject}
          </button>
        </>
      ) : null}
      {data.status === 'approved' ? (
        <button
          type="button"
          onClick={() => onRevokeAccess(data)}
          className="py-1 px-3 rounded-md text-sm font-medium text-white bg-red-500 hover:bg-red-600"
        >
          {labels.revokeAccess}
        </button>
      ) : null}
      {data.status === 'inactive' ? (
        <button
          type="button"
          onClick={() => onReactivate(data)}
          className="py-1 px-3 rounded-md text-sm font-medium text-white bg-primary hover:bg-secondary"
        >
          {labels.reactivate}
        </button>
      ) : null}
    </div>
  ),
});

export const serviceCallApplicationColumn = {
  officialName,
  businessId,
  contactName,
  email,
  status,
  createdAt,
  handledAt,
  link,
  actions,
};
